import React, { useState } from 'react';

// Предустановленные периоды загрузки
const LOAD_PERIODS = [
  { id: 'all', name: 'Все время', description: 'Загрузить все записи (может быть медленно)', days: null },
  { id: 'week', name: 'Неделя', description: 'Последние 7 дней', days: 7 },
  { id: 'month', name: 'Месяц', description: 'Последние 30 дней', days: 30 },
  { id: 'quarter', name: 'Квартал', description: 'Последние 90 дней', days: 90 },
  { id: 'half_year', name: 'Полгода', description: 'Последние 180 дней', days: 180 },
  { id: 'year', name: 'Год', description: 'Последние 365 дней', days: 365 },
  { id: 'custom', name: 'Период', description: 'Выбрать свой период', days: 'custom' }
];

const DataLoadSettings = ({ onLoadData, loading }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('month'); // По умолчанию месяц
  const [customRange, setCustomRange] = useState({ from: '', to: '' });
  const [showSettings, setShowSettings] = useState(false);

  // Функция для расчета даты "от" на основе выбранного периода
  const getDateRange = (periodId) => {
    const now = new Date();
    const currentPeriod = LOAD_PERIODS.find(p => p.id === periodId);
    
    if (!currentPeriod || periodId === 'all') {
      return { from: null, to: null };
    }
    
    if (periodId === 'custom') {
      return {
        from: customRange.from || null,
        to: customRange.to || null
      };
    }
    
    const fromDate = new Date(now.getTime() - currentPeriod.days * 24 * 60 * 60 * 1000);
    return {
      from: fromDate.toISOString().split('T')[0],
      to: now.toISOString().split('T')[0]
    };
  };

  // Обработчик загрузки данных
  const handleLoadData = () => {
    const dateRange = getDateRange(selectedPeriod);
    onLoadData(dateRange);
    setShowSettings(false);
  };

  // Получить описание текущего периода
  const getCurrentPeriodDescription = () => {
    const currentPeriod = LOAD_PERIODS.find(p => p.id === selectedPeriod);
    const dateRange = getDateRange(selectedPeriod);
    
    if (selectedPeriod === 'all') {
      return 'За все время';
    }
    
    if (selectedPeriod === 'custom' && dateRange.from && dateRange.to) {
      return `С ${new Date(dateRange.from).toLocaleDateString('ru-RU')} по ${new Date(dateRange.to).toLocaleDateString('ru-RU')}`;
    }
    
    if (dateRange.from && dateRange.to) {
      return `${currentPeriod.name} (с ${new Date(dateRange.from).toLocaleDateString('ru-RU')})`;
    }
    
    return currentPeriod?.name || 'Не выбран';
  };

  return (
    <div className="relative">
      {/* Кнопка настроек загрузки */}
      <button
        onClick={() => setShowSettings(!showSettings)}
        className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Настройки загрузки
        <svg className={`ml-2 h-4 w-4 transform transition-transform ${showSettings ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Текущий период */}
      <div className="mt-1 text-xs text-gray-500">
        Период: {getCurrentPeriodDescription()}
      </div>

      {/* Панель настроек */}
      {showSettings && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900">
                Настройки загрузки данных
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Выбор периода */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Выберите период для загрузки:
              </label>
              
              {LOAD_PERIODS.map(period => (
                <label key={period.id} className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    name="loadPeriod"
                    value={period.id}
                    checked={selectedPeriod === period.id}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">{period.name}</div>
                    <div className="text-xs text-gray-500">{period.description}</div>
                  </div>
                </label>
              ))}
            </div>

            {/* Кастомный период */}
            {selectedPeriod === 'custom' && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Выберите свой период:
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Дата от</label>
                    <input
                      type="date"
                      value={customRange.from}
                      onChange={(e) => setCustomRange(prev => ({ ...prev, from: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Дата до</label>
                    <input
                      type="date"
                      value={customRange.to}
                      onChange={(e) => setCustomRange(prev => ({ ...prev, to: e.target.value }))}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Предупреждение для "Все время" */}
            {selectedPeriod === 'all' && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <svg className="w-4 h-4 text-yellow-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Внимание</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Загрузка всех данных может занять много времени и потребовать больше памяти.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Кнопки управления */}
            <div className="flex justify-between pt-4 border-t mt-4">
              <button
                onClick={() => setShowSettings(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
              >
                Отмена
              </button>
              
              <button
                onClick={handleLoadData}
                disabled={loading || (selectedPeriod === 'custom' && (!customRange.from || !customRange.to))}
                className="px-4 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white inline mr-1"></div>
                    Загрузка...
                  </>
                ) : (
                  'Загрузить данные'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataLoadSettings;